import { subscriptionTypes } from '@constants/index';

const getPlusSubscriptionsUids = (user) =>
  user?.latestUserSubscriptions?.reduce((_arrayIds, _plusSubscription) => {
    if (_plusSubscription.time_remaining > 0)
      return [..._arrayIds, _plusSubscription.subscription.value.uid];
    return _arrayIds;
  }, []) ?? [];

const getRelativeLink = (user) => {
  let url = `/learner/@${user.username}`;
  if (user.is_educator || user.is_moderator)
    url = user.relative_link || `/@${user.username}`;
  return url;
};

const getExpiredSubscriptionUids = (user) =>
  user?.latestUserSubscriptions?.reduce((_arrayIds, _plusSubscription) => {
    if (_plusSubscription.time_remaining <= 0)
      return [..._arrayIds, _plusSubscription.subscription.value.uid];
    return _arrayIds;
  }, []) ?? [];

const getExpiredIconicUIDs = (user) =>
  user?.latestUserSubscriptions?.reduce((_arrayIds, _plusSubscription) => {
    if (
      _plusSubscription.time_remaining <= 0 &&
      _plusSubscription.subscription.type === subscriptionTypes.ICONIC
    )
      return [..._arrayIds, _plusSubscription.subscription.value.uid];
    return _arrayIds;
  }, []) ?? [];

const getIconicSubscriptionsUids = (user) =>
  user?.latestUserSubscriptions?.reduce((_arrayIds, _plusSubscription) => {
    if (
      _plusSubscription.time_remaining > 0 &&
      _plusSubscription.subscription.type === subscriptionTypes.ICONIC
    )
      return [..._arrayIds, _plusSubscription.subscription.value.uid];
    return _arrayIds;
  }, []) ?? [];

class Me {
  constructor(user) {
    this.firstName = user.first_name?.toLocaleString() ?? '';
    this.lastName = user.last_name ?? '';
    this.uid = user.uid;
    this.userID = user.user_id;
    this.name = `${this.firstName} ${this.lastName}`;
    this.thumbnail = user.avatar || null;
    this.description = user.bio || null;
    this.gender = user.gender;
    this.isFullStoryUser = user.is_full_story_user;
    this.phone = user.phone;
    this.email = user.email;
    this.state = user.state;
    this.country = user.country;
    this.language = user.language;

    this.relativeLink = getRelativeLink(user);

    this.username = user.username || null;
    this.isEducator = user.is_educator;
    this.isVerifiedEducator = user.is_verified_educator;
    this.isEmailVerified = user.is_email_verified;
    this.isPhoneVerified = user.is_phone_verified;
    this.isProfileFilled = user.is_profile_filled;
    this.isModerator = user.is_moderator;
    this.isActive = user.is_active;
    this.isFreeOfflinePromotionCreditsAvailable =
      user.is_free_offline_promotion_credits_available;
    this.isSystemUser = user.is_system_user;
    this.isIntercomEnabled = user.is_intercom_enabled;
    this.isPlusEnrolled = user.is_plus_enrolled;
    this.isAnonymous = user.is_anonymous;
    this.isKycCompleted = user.is_kyc_completed;
    this.isPlusEducator = user.is_plus_educator;
    this.socialSharing = user.social_sharing;
    this.referralInfo = user.referral_info;
    this.isPasswordSet = user.is_password_set;
    this.boardingSteps = user.boarding_steps_v3;
    this.messengerConnected = user.messenger_connected;
    this.hasAcceptedTNC = user.has_accepted_tnc;
    this.notificationToken = user.notification_token;
    this.feedUpdatesDisabled = user.feed_updates_disabled;
    this.credits = user.credits;
    this.educatorApprovalVotingState = user.educator_approval_voting_state;
    this.subscription = user.subscription;
    this.dateJoined = user.date_joined;
    this.activityDetails = user.activity_details;
    this.plusSubscriptions = user.plus_subscriptions;
    this.latestUserSubscriptions = user.latest_user_subscriptions_type_wise;
    this.currentEpoch = user.current_epoch;
    this.hasAcceptedAggrement = user.has_accepted_aggrement;
    this.jwtToken = user.jwt_token;
    this.channels = user.channels;
    this.kycDetails = user.kyc_details;
    this.videoCallAllowed = user.video_call_allowed;
    this.videoCallEnabled = user.video_call_enabled;
    this.videoFlag = user.video_flag;
    this.chatWithUsFlag = user.chat_with_us_flag;
    this.canAddSpecialClass = user.can_add_special_class;
    this.isFirstSpecialClass = user.is_first_special_class;
    this.searchJwtToken = user.search_jwt_token;
    this.canAddPlusCourse = user.can_add_plus_course;
    this.plusSubscriptionUids = getPlusSubscriptionsUids(this);
    this.expiredSubscriptionsUids = getExpiredSubscriptionUids(this);
    this.expiredIconicUIDs = getExpiredIconicUIDs(this);
    this.iconicSubscriptionUIDs = getIconicSubscriptionsUids(this);
    this.isSchoolEducator = user.is_un_teach_user;
    this.isExternalContentCreator = user.is_external_content_creator;
    this.platformUnlockStatus = user.platform_unlock;
    this.isInvited = user.is_invited || false;
  }
}

const getEntityData = (data) => {
  const me = new Me(data);
  return me;
};

export const hasActiveSubscriptionToGoal = (data, uid, subscriptionType) => {
  /* this function is used for finding a active subscription to goal
    if we need to find whether its iconic or regular we can send the
    subscription_active param too
  */

  if (!data.uid || !uid) return false;
  const filteredData = data.latestUserSubscriptions.filter(
    (item) => item.subscription?.value.uid === uid && item.time_remaining > 0
  );
  if (subscriptionType && filteredData.length) {
    const subscription = filteredData.find(
      (sub) => sub.subscription.type === subscriptionType
    );
    if (subscription) return true;
    return false;
  }
  return Boolean(filteredData.length);
};

export const hasActiveIconicSubscriptionToGoal = (data, uid) => {
  if (!data.uid || !uid) return false;
  const filteredData = data.latestUserSubscriptions.find(
    (item) =>
      item.subscription?.value.uid === uid &&
      item.time_remaining > 0 &&
      item.subscription.type === 5
  );
  return Boolean(filteredData);
};

const hasExpiredSubscriptionToGoal = (data, uid) => {
  /* this function is used for finding a expired subscription to a  goal */

  if (!data.uid || !uid) return false;
  const filteredData = data.expiredSubscriptionsUids.find(
    (item) => item === uid
  );
  return Boolean(filteredData);
};

const hasExpiredIconicSubscriptionToGoal = (data, uid) => {
  /* this function is used for finding a expired subscription to a iconic goal */

  if (!data.uid || !uid) return false;
  const filteredData = data.expiredIconicUIDs.find((item) => item === uid);
  return Boolean(filteredData);
};

export const hasClassAccess = (data, classDetails = {}) => {
  if (data.uid) {
    const programme = classDetails.programme || { goal: {} };
    if (classDetails.isSpecial || programme.isSpecial) return true;
    const goalUID =
      programme.goalUid || classDetails.goalUid || programme.goal.uid;
    return hasActiveSubscriptionToGoal(data, goalUID);
  }
  return false;
};

/* getSubscription(subscriptions,goalUID) -> find subscription of the goal which has goalUID */
export const getSubscription = (subscriptions, goalUID) =>
  subscriptions?.find((data) => data.subscription?.value?.uid === goalUID) ??
  null;

const getExpiryDate = (subscriptions, goalUID) =>
  subscriptions?.find((data) => data.subscription?.value?.uid === goalUID)
    ?.expired_at ?? null;

export const getDaysRemainingInSubscription = (subscriptions, goalUID) => {
  const currentSubscription = subscriptions?.find(
    (subscription) => subscription.subscription?.value?.uid === goalUID
  );
  return currentSubscription?.time_remaining / (3600 * 24) ?? 0;
};

export default {
  Me,
  getEntityData,
  hasClassAccess,
  hasActiveSubscriptionToGoal,
  hasActiveIconicSubscriptionToGoal,
  hasExpiredSubscriptionToGoal,
  hasExpiredIconicSubscriptionToGoal,
  getExpiryDate,
  getSubscription,
  getDaysRemainingInSubscription
};
